import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import { useMinistries } from '../data/useMinistries';
import { ministryPhotoUrl } from '../lib/supabase';
import { pickLang } from '../utils/pickLang';
import { churchPhotos } from '../data/churchPhotos';
import Hero from '../components/redesign/Hero';
import Section from '../components/redesign/Section';

const SERVE_TEAM_FORM = 'https://churchofnewhope.churchcenter.com/people/forms/922679';

const LANGUAGE_LABEL: Record<'en' | 'uk' | 'bilingual', string> = {
  en: 'English',
  uk: 'Ukrainian',
  bilingual: 'Bilingual',
};

interface MinistryItem {
  id: string;
  name: string;
  description?: string;
  meeting?: string;
  image?: string;
  icon?: string;
  audience?: string;
  language?: 'en' | 'uk' | 'bilingual';
  slug?: string;
  isFeatured?: boolean;
  ctaUrl?: string;
  ctaLabel?: string;
  contactEmail?: string;
}

/**
 * Local sample ministries shown when the database has none yet.
 * Used for previews/presentations — images are real church photos (churchPhotos.ts),
 * but names and descriptions are placeholders. No meeting times until real data is in the DB.
 */
function getSampleMinistries(language: 'en' | 'uk'): MinistryItem[] {
  const uk = language === 'uk';
  return [
    {
      id: 'sample-worship',
      name: uk ? 'Група прославлення' : 'Worship & Praise Team',
      description: uk
        ? 'Музиканти й вокалісти, які ведуть громаду в поклонінні на щотижневих богослужіннях'
        : 'Musicians and vocalists who lead the congregation in worship every Sunday',
      image: churchPhotos.worshipTeam.src(800),
    },
    {
      id: 'sample-choir',
      name: uk ? 'Хор' : 'Choir',
      description: uk
        ? 'Спільний спів, що наповнює служіння хвалою — приєднуйтесь незалежно від досвіду'
        : 'Voices joined together in praise – all are welcome, no experience needed',
      image: churchPhotos.choir.src(800),
    },
    {
      id: 'sample-children',
      name: uk ? "Дитяче служіння" : "Children's Ministry",
      description: uk
        ? 'Безпечне й радісне місце, де діти пізнають Ісуса через ігри, історії та творчість'
        : 'A safe, joyful place where kids learn about Jesus through games, stories, and crafts',
      image: churchPhotos.kids.src(800),
    },
    {
      id: 'sample-sunday-school',
      name: uk ? 'Недільна школа' : 'Sunday School',
      description: uk
        ? 'Вивчення Біблії для всіх вікових груп, щоб зростати у вірі та пізнанні Слова'
        : 'Bible classes for every age to grow in faith and understanding of God’s Word',
      image: churchPhotos.sundaySchool.src(800),
    },
    {
      id: 'sample-youth',
      name: uk ? 'Молодіжне служіння' : 'Youth Ministry',
      description: uk
        ? 'Спільнота для підлітків і молоді: спілкування, прославлення та зростання у Христі'
        : 'A community for teens and young adults to connect, worship, and grow in Christ',
      image: churchPhotos.youth.src(800),
    },
    {
      id: 'sample-groups',
      name: uk ? 'Малі групи' : 'Small Groups',
      description: uk
        ? 'Невеликі домашні групи для спільної молитви, вивчення Біблії та підтримки одне одного'
        : 'Home groups gathering for prayer, Bible study, and doing life together',
      image: churchPhotos.homeGroup.src(800),
    },
    {
      id: 'sample-prayer',
      name: uk ? 'Молитовне служіння' : 'Prayer Ministry',
      description: uk
        ? 'Команда, що молиться за потреби громади та підтримує кожного, хто потребує молитви'
        : 'A team devoted to praying over the needs of our church family and community',
      image: churchPhotos.prayingTogether.src(800),
    },
    {
      id: 'sample-hospitality',
      name: uk ? 'Служіння гостинності' : 'Hospitality & Welcome',
      description: uk
        ? 'Зустрічаємо гостей з усмішкою, допомагаємо кожному відчути себе вдома у New Hope'
        : 'Greeting guests with a smile and helping everyone feel at home at New Hope',
      image: churchPhotos.hospitality.src(800),
    },
  ];
}

const cardLinkClass =
  'mt-6 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-tan-500 hover:gap-3 transition-all';

const MinistryCard: React.FC<{
  item: MinistryItem;
  index: number;
  contactLabel: string;
  learnMoreLabel: string;
  lang: 'en' | 'uk';
}> = ({ item, index, contactLabel, learnMoreLabel, lang }) => {
  const langBadge = item.language ? LANGUAGE_LABEL[item.language] : undefined;
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: (index % 3) * 0.08 }}
      className="group flex flex-col overflow-hidden bg-white border border-navy-900/10 hover:border-tan-500 hover:shadow-2xl hover:shadow-navy-900/10 transition-all"
    >
      {item.image ? (
        <div className="relative aspect-[4/3] overflow-hidden">
          <img
            src={item.image}
            alt={item.name}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-navy-900/40 to-transparent" />
          <div className="absolute bottom-0 left-0 h-[3px] w-14 bg-tan-500" aria-hidden />
        </div>
      ) : (
        <div className="p-8 pb-0">
          <div className="flex h-14 w-14 items-center justify-center bg-navy-50 text-navy-700 group-hover:bg-tan-500 group-hover:text-white transition-colors">
            <i className={`fas fa-${item.icon || 'users'} text-xl`} />
          </div>
        </div>
      )}

      <div className="flex flex-1 flex-col p-8">
        <div className="flex items-start justify-between gap-3">
          <h3 className="font-display text-xl font-bold uppercase tracking-wider text-navy-900">
            {item.name}
          </h3>
          {langBadge && (
            <span className="mt-1 shrink-0 rounded-full border border-navy-900/15 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-navy-700/70">
              {langBadge}
            </span>
          )}
        </div>
        {item.audience && (
          <p className="mt-2 text-xs font-semibold uppercase tracking-wider text-tan-600">{item.audience}</p>
        )}
        {item.description && (
          <p className="mt-4 text-sm leading-relaxed text-navy-700/85">{item.description}</p>
        )}
        {item.meeting && (
          <p className="mt-4 text-xs uppercase tracking-wider text-navy-700/60">{item.meeting}</p>
        )}

        {item.isFeatured && item.slug ? (
          <Link to={`/${lang}/ministries/${item.slug}`} className={cardLinkClass}>
            {learnMoreLabel} →
          </Link>
        ) : item.ctaUrl ? (
          <a href={item.ctaUrl} target="_blank" rel="noopener noreferrer" className={cardLinkClass}>
            {item.ctaLabel || contactLabel} →
          </a>
        ) : item.contactEmail ? (
          <a href={`mailto:${item.contactEmail}`} className={cardLinkClass}>
            {contactLabel} →
          </a>
        ) : null}
      </div>
    </motion.div>
  );
};

const Ministries: React.FC = () => {
  const { t, language } = useLanguage();
  const { data: ministries, loading } = useMinistries();

  const dbItems: MinistryItem[] = ministries.map((m) => ({
    id: m.id,
    name: pickLang(language, m.name_en, m.name_uk),
    description: pickLang(language, m.description_en, m.description_uk) || undefined,
    meeting: pickLang(language, m.meeting_info_en, m.meeting_info_uk) || undefined,
    image: m.photo_path ? ministryPhotoUrl(m.photo_path) : undefined,
    icon: m.icon || undefined,
    audience: pickLang(language, m.audience_en ?? '', m.audience_uk ?? '') || undefined,
    language: m.language,
    slug: m.slug,
    isFeatured: m.is_featured,
    ctaUrl: m.cta_url || SERVE_TEAM_FORM,
    ctaLabel: pickLang(language, m.cta_label_en ?? '', m.cta_label_uk ?? '') || undefined,
    contactEmail: m.contact_email || undefined,
  }));

  const items = dbItems.length > 0 ? dbItems : getSampleMinistries(language);

  return (
    <div className="bg-cream">
      <Hero
        image={churchPhotos.worshipStage.src()}
        eyebrow={t('ministries.hero.eyebrow')}
        scriptAccent={t('ministries.hero.script')}
        title={t('ministries.hero.title')}
        description={t('ministries.hero.description')}
        height="short"
      />

      <Section variant="cream" padding="lg">
        {loading ? (
          <p className="text-center text-navy-700/60">Loading…</p>
        ) : (
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {items.map((item, i) => (
              <MinistryCard
                key={item.id}
                item={item}
                index={i}
                lang={language}
                contactLabel={t('ministries.contact')}
                learnMoreLabel={t('ministries.learnMore')}
              />
            ))}
          </div>
        )}
      </Section>
    </div>
  );
};

export default Ministries;
